import { useLayoutEffect } from 'react';
import animationConfig from '../config/animationClasses.json';

export const ANIMATION_CLASS_GROUPS = Object.freeze(animationConfig.groups || []);
export const ANIMATION_CLASSES = Object.freeze(animationConfig.classes || []);
export const ANIMATION_CLASS_NAMES = Object.freeze(
  ANIMATION_CLASSES.map(({ className }) => className)
);
export const SCROLL_ANIMATION_CLASS_NAMES = Object.freeze(
  ANIMATION_CLASSES
    .filter(({ group }) => group === 'scroll')
    .map(({ className }) => className)
);
export const ANIMATION_AI_CONTEXT = ANIMATION_CLASSES
  .map(({ className, label }) => `${className} (${label})`)
  .join(', ');

const animationClassSet = new Set(ANIMATION_CLASS_NAMES);
const scrollSelector = SCROLL_ANIMATION_CLASS_NAMES
  .map((className) => `.${className}`)
  .join(',');

export const isEditorAnimationClass = (className) => (
  animationClassSet.has(String(className || '').trim())
);

const findAnimatedElements = (root) => {
  if (!root || !scrollSelector) return [];
  return [
    ...(root.matches?.(scrollSelector) ? [root] : []),
    ...root.querySelectorAll(scrollSelector),
  ];
};

export const useScrollAnimations = (rootRef, documentRoot) => {
  useLayoutEffect(() => {
    const root = rootRef.current;
    const elements = findAnimatedElements(root);
    root?.querySelectorAll?.('.motion-scroll-pending, .motion-scroll-visible')
      .forEach((element) => {
        if (!element.matches(scrollSelector)) {
          element.classList.remove('motion-scroll-pending', 'motion-scroll-visible');
        }
      });
    if (!elements.length) return undefined;

    const reveal = (element) => {
      element.classList.add('motion-scroll-visible');
      element.classList.remove('motion-scroll-pending');
    };

    if (
      typeof IntersectionObserver !== 'function'
      || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      elements.forEach(reveal);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08,
    });

    elements
      .filter((element) => !element.classList.contains('motion-scroll-visible'))
      .forEach((element) => {
        element.classList.add('motion-scroll-pending');
        observer.observe(element);
      });

    return () => {
      observer.disconnect();
    };
  }, [documentRoot, rootRef]);
};
